import { useState } from "react";
import { Drawer, Button } from "antd";

import StarCardIcon from "@/assets/icons/star_card.svg?react";
import InfoTooltip from "../../InfoTooltip";
import { Workspace, WorkspaceList } from "./workspace";
import { WorkspaceItem } from "./WorkspaceItem";
import { WorkspaceModal } from "./SaveWorkspaceButton/WorkspaceModal";
import { UnsavedChangesModal } from "./UnsavedChangesModal";
import { TableState, ColumnVisibility } from "@/types/tableState.types";
import { WorkspaceConfig } from "@/types/workspaceConfig";
import { sortWorkspaces } from "@/utils/workspaceUtils";
import { useSuperuserMode } from "@/contexts/useSuperuser";
import { getWorkspaceModalCopy } from "./workspaceFlow";

type WorkspaceDrawerProps = {
  open: boolean;
  onClose: () => void;
  workspaces: WorkspaceList;
  selectedWorkspaceId: number;
  onSelectWorkspace: (workspaceId: number) => void;
  onDuplicate?: (
    title: string,
    configuration: WorkspaceConfig,
    isVivoTemplate?: boolean,
  ) => void;
  onRename?: (
    workspaceId: number,
    newTitle: string,
    isVivoTemplate?: boolean,
  ) => void;
  onDelete?: (workspaceId: number) => void;
  onCreateNew?: (
    title: string,
    configuration: WorkspaceConfig,
    isVivoTemplate?: boolean,
  ) => void;
  onSaveAsNewWorkspace?: (name: string) => Promise<void>;
  onSaveCurrentWorkspace?: () => Promise<void> | void;
  onApplyLatestTemplate?: () => Promise<void> | void;
  currentTableState?: TableState;
  hasUnsavedChanges?: boolean;
  hasDrift?: boolean;
};

export function WorkspaceDrawer({
  open,
  onClose,
  workspaces,
  selectedWorkspaceId,
  onSelectWorkspace,
  onDuplicate,
  onRename,
  onDelete,
  onCreateNew,
  onSaveAsNewWorkspace,
  onSaveCurrentWorkspace,
  onApplyLatestTemplate,
  currentTableState,
  hasUnsavedChanges = false,
  hasDrift = false,
}: WorkspaceDrawerProps) {
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [workspaceToDuplicate, setWorkspaceToDuplicate] =
    useState<Workspace | null>(null);
  const [pendingWorkspaceId, setPendingWorkspaceId] = useState<number | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const { isSuperuserMode } = useSuperuserMode();

  const sortedWorkspaces = sortWorkspaces(workspaces);
  const existingNames = workspaces.map((w) => w.title);
  const activeWorkspace = workspaces.find((w) => w.id === selectedWorkspaceId);
  const modalCopy = getWorkspaceModalCopy({
    isDefault: false,
    isVivoTemplate: !!activeWorkspace?.is_vivo_template,
    isSuperuserMode,
    hasDrift,
  });

  const handleSelectWorkspace = (workspaceId: number) => {
    if (workspaceId === selectedWorkspaceId) return;
    if (!hasUnsavedChanges && !hasDrift) {
      onSelectWorkspace(workspaceId);
    } else {
      setPendingWorkspaceId(workspaceId);
    }
  };

  const handleDuplicate = (workspaceId: number) => {
    const workspace = workspaces.find((w) => w.id === workspaceId);
    if (workspace) {
      setWorkspaceToDuplicate(workspace);
    }
  };

  const handleRename = (
    workspaceId: number,
    newTitle: string,
    isVivoTemplate?: boolean,
  ) => {
    if (onRename) {
      onRename(workspaceId, newTitle, isVivoTemplate);
    }
  };

  const handleDelete = (workspaceId: number) => {
    if (onDelete) {
      onDelete(workspaceId);
    }
  };

  const handleCreateNew = () => {
    setIsCreateModalOpen(true);
  };

  const handleAfterSave = (workspaceId: number | null) => {
    if (workspaceId === null) return;
    onSelectWorkspace(workspaceId);
    setPendingWorkspaceId(null);
  };

  const getWorkspaceAction = (): (() => void) | undefined => {
    if (
      modalCopy?.workspaceActionType === "saveCurrent" &&
      onSaveCurrentWorkspace
    ) {
      return async () => {
        if (pendingWorkspaceId === null) return;
        setIsSaving(true);
        try {
          await onSaveCurrentWorkspace();
          handleAfterSave(pendingWorkspaceId);
        } finally {
          setIsSaving(false);
        }
      };
    }

    if (
      modalCopy?.workspaceActionType === "applyLatest" &&
      onApplyLatestTemplate
    ) {
      return async () => {
        if (pendingWorkspaceId === null) return;
        setIsSaving(true);
        try {
          await onApplyLatestTemplate();
          handleAfterSave(pendingWorkspaceId);
        } finally {
          setIsSaving(false);
        }
      };
    }

    return undefined;
  };

  const getDefaultConfiguration = (): WorkspaceConfig => {
    const columns = currentTableState
      ? currentTableState.columns.map((col) => ({
          ...col,
          visibility:
            col.visibility === ColumnVisibility.NOT_AVAILABLE
              ? ColumnVisibility.NOT_AVAILABLE
              : ColumnVisibility.VISIBLE,
        }))
      : [];

    return {
      global: { filters: {} },
      tabs: [
        {
          name: "Table",
          type: "table",
          config: {
            columns,
            lockedColumns: 0,
            sorting: [],
            pagination: { current: 1, pageSize: 100 },
          },
        },
        {
          name: "Dashboard",
          type: "dashboard",
          config: [],
        },
      ],
      version: "2.0.0",
    };
  };

  return (
    <>
      <WorkspaceModal
        isOpen={isCreateModalOpen}
        onCancel={() => setIsCreateModalOpen(false)}
        onConfirm={(name, isVivoTemplate) => {
          if (onCreateNew) {
            onCreateNew(name, getDefaultConfiguration(), isVivoTemplate);
          }
          setIsCreateModalOpen(false);
        }}
        existingNames={existingNames}
      />
      <WorkspaceModal
        isOpen={workspaceToDuplicate !== null}
        onCancel={() => setWorkspaceToDuplicate(null)}
        onConfirm={(name, isVivoTemplate) => {
          if (onDuplicate && workspaceToDuplicate) {
            onDuplicate(
              name,
              workspaceToDuplicate.configuration,
              isVivoTemplate,
            );
          }
          setWorkspaceToDuplicate(null);
        }}
        defaultValue={
          workspaceToDuplicate ? `${workspaceToDuplicate.title} (copy)` : ""
        }
        existingNames={existingNames}
      />
      {pendingWorkspaceId !== null && modalCopy && (
        <UnsavedChangesModal
          isOpen
          isSaving={isSaving}
          title={modalCopy.title}
          description={modalCopy.description}
          workspaceName={activeWorkspace?.title ?? ""}
          discardLabel={modalCopy.discardLabel}
          saveAsNewLabel={modalCopy.saveAsNewLabel}
          saveAsNewConfirmLabel={modalCopy.saveAsNewConfirmLabel}
          saveCurrentWorkspaceLabel={modalCopy.saveCurrentWorkspaceLabel}
          saveCurrentWorkspaceConfirmLabel={
            modalCopy.saveCurrentWorkspaceConfirmLabel
          }
          saveCurrentWorkspaceWarning={modalCopy.saveCurrentWorkspaceWarning}
          applyLatestTemplateLabel={modalCopy.applyLatestTemplateLabel}
          onCancel={() => setPendingWorkspaceId(null)}
          onDiscard={async () => {
            setIsSaving(true);
            try {
              handleAfterSave(pendingWorkspaceId);
            } finally {
              setIsSaving(false);
            }
          }}
          onSaveAsNew={async (copyName) => {
            if (!onSaveAsNewWorkspace || pendingWorkspaceId === null) return;
            setIsSaving(true);
            try {
              await onSaveAsNewWorkspace(copyName);
              handleAfterSave(pendingWorkspaceId);
            } finally {
              setIsSaving(false);
            }
          }}
          onWorkspaceAction={getWorkspaceAction()}
        />
      )}
      <Drawer
        open={open}
        onClose={onClose}
        size={400}
        className="workspace-drawer"
        title={
          <div className="flex items-center gap-2">
            <span className="text-[#2B4752] font-semibold text-lg">
              Workspaces
            </span>
            <InfoTooltip title="Workspaces are saved configurations of data and dashboards, letting you quickly return to the exact layout, plots and filters you need." />
          </div>
        }
        styles={{
          body: { padding: 0 },
        }}
      >
        <div
          data-testid="workspace-drawer"
          className="flex flex-col py-3 px-4 gap-4 "
        >
          <div className=" py-4 px-5 border border-[#EBEBEB] bg-[#FAFAFA] rounded-md flex flex-col justify-between items-center">
            <div className="flex items-center gap-3 mb-4">
              <div className="">
                <StarCardIcon className="text-[#5F6368] w-6 h-6" />
              </div>
              <p className="text-[#5F6368] text-sm">
                Start with a full dataset and a blank dashboard ready for your
                customizations.
              </p>
            </div>
            <Button
              data-testid="workspace-create-button"
              type="primary"
              className="w-fit"
              onClick={handleCreateNew}
              style={{
                backgroundColor: "#D2EFF9",
                borderColor: "#06709B",
                color: "#06709B",
              }}
            >
              Create new workspace
            </Button>
          </div>

          <div className="flex flex-col gap-2">
            {sortedWorkspaces.map((workspace) => (
              <WorkspaceItem
                key={workspace.id}
                workspace={workspace}
                isSelected={selectedWorkspaceId === workspace.id}
                isMenuOpen={openMenuId === workspace.id}
                onSelect={handleSelectWorkspace}
                onMenuOpenChange={(open) =>
                  setOpenMenuId(open ? workspace.id : null)
                }
                onDuplicate={handleDuplicate}
                onRename={handleRename}
                onDelete={handleDelete}
                existingNames={existingNames}
              />
            ))}
          </div>
        </div>
      </Drawer>
    </>
  );
}
